<div class="flex flex-col min-h-screen p-6" *transloco="let t">
  <!-- Título da Página -->
  <div class="mb-6">
    <h4 class="text-2xl font-bold text-gray-800">{{ t('invoices.title') }}</h4>
    <p class="text-sm text-gray-600 mt-1">{{ t('invoices.totalCount', { count: totalInvoices }) }}</p>
  </div>

  <!-- Grid de Faturas -->
  <div #invoicesContainer class="flex-grow overflow-auto" style="max-height: calc(100vh - 200px);" (scroll)="onScroll($event)">
    <div *ngIf="invoices.length === 0 && !loadingInvoices" class="text-center py-16">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p class="mt-4 text-gray-500 text-lg">{{ t('invoices.empty') }}</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <div 
        *ngFor="let invoice of invoices" 
        (click)="openInvoiceDetail(invoice)"
        class="bg-white border border-gray-300 hover:border-blue-400 p-5 hover:shadow-lg cursor-pointer transition-all duration-200 transform hover:-translate-y-1">
        
        <!-- Header do Card -->
        <div class="flex justify-between items-start mb-3">
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <h5 class="font-semibold text-lg text-gray-800">
                {{ t('invoices.invoiceLabel') }} #{{ invoice.invoiceNumber }}
              </h5>
              <svg *ngIf="isOverdue(invoice)" class="w-5 h-5 text-red-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <span [ngClass]="getStatusClass(invoice.status)" class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2">
            {{ getStatusText(invoice.status) }}
          </span>
        </div>

        <!-- Valor Total -->
        <div class="mb-4">
          <p class="text-xs mb-1 text-gray-500">{{ t('invoices.totalAmount') }}</p>
          <p class="text-2xl font-bold text-green-600">{{ formatCurrency(invoice.totalAmount) }}</p>
        </div>

        <!-- Informações Rápidas -->
        <div class="space-y-2 mb-4">
          <div class="flex items-center text-sm text-gray-600">
            <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span [ngClass]="isOverdue(invoice) ? 'text-red-600 font-semibold' : ''">{{ t('invoices.due') }} {{ invoice.dueDate | date:'dd/MM/yyyy' }}</span>
            <span *ngIf="isOverdue(invoice)" class="ml-2 text-xs font-bold text-red-600 animate-pulse">⚠️ {{ t('invoices.overdue') }}</span>
          </div>
          <div class="flex items-center text-sm text-gray-600">
            <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>{{ t('invoices.itemsCount', { count: invoice.items.length || 0 }) }}</span>
          </div>
        </div>

        <!-- Footer do Card -->
        <div class="pt-3 border-t border-gray-200">
          <ng-container *appHasPermission="'invoices.pay'">
            <button 
              *ngIf="canPay(invoice)"
              (click)="payNow($event, invoice)"
              class="w-full px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium">
              {{ t('invoices.payNow') }}
            </button>
          </ng-container>
          <p *ngIf="!canPay(invoice)" class="text-xs text-gray-400 text-center">{{ t('invoices.clickForDetails') }}</p>
        </div>
      </div>
    </div>

    <!-- Indicador de Carregamento -->
    <div *ngIf="loadingInvoices" class="text-center py-8">
      <div class="inline-flex items-center">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-500">{{ t('invoices.loadingMore') }}</span>
      </div>
    </div>
  </div>
</div>

